/*
  "localizer" is the react-big-calendar's way to provide an adapter for different time
  libraries. It's a function that returns an object with methods that are used by the
  calendar internally.
  https://github.com/jquense/react-big-calendar/tree/master?tab=readme-ov-file#localization-and-date-formatting
  The library provides several localizers out of the box – including versions
  for dayjs, moment, luxon, date-fns and others.

  Theoretically, a proper localizer would be able to handle all possible issues,
  including the shifting of time intervals related to passing
  the daylight-saving time date, which are causing incorrect display of events
  in the calendar.

  Initially, dayjsLocalizer was used, but it turned out that it didn't handle DST issues properly.
  The grid was rendered incorrectly on the DST day, and the events got shifted 1 hour later if they
  passed through DST date (March 9th).
  Using momentLocalizer would require to install moment.js which is quite heavy library,
  so I decided to avoid it, since it also wasn't guaranteed to solve all the issues.
  Then I tried luxonLocalizer and it had the same issues as dayjs.

  Finally, datefnsLocalizer was used, and it resolved the visual issues with the grid and shifting of events,
  but it was less feature-complete than dayjsLocalizer. It didn't have the `inRange` method, which is used to
  determine if a date is within the range of two other dates.

  So in the end I've decided to build a custom localizer based on existing dateFnsLocalizers by combining the approaches of those 2 solutions, which turned
  out to work correctly. Most of this code is copied from here – https://github.com/jquense/react-big-calendar/tree/master/src/localizers,
  specifically – https://github.com/jquense/react-big-calendar/blob/master/src/localizers/date-fns.js and
  https://github.com/jquense/react-big-calendar/blob/master/src/localizers/dayjs.js.
  I've combined methods from those 2 libraries and added some TS typings where it was possible.
*/
import type { Locale } from 'date-fns';
import { format } from 'date-fns/format';
import { getDay } from 'date-fns/getDay';
import { enUS } from 'date-fns/locale/en-US';
import { startOfWeek } from 'date-fns/startOfWeek';

import dayjs, { type Dayjs, type ManipulateType, type OpUnitType } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { DateLocalizer } from 'react-big-calendar';
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

type FormatterFn = (
  dates: { start: Date; end: Date },
  culture: string,
  local: {
    format: (date: Date, format: string, culture: string) => string;
  },
) => string;
const dateRangeFormat: FormatterFn = ({ start, end }, culture, local) =>
  `${local.format(start, 'P', culture)} – ${local.format(end, 'P', culture)}`;

const timeRangeFormat: FormatterFn = ({ start, end }, culture, local) =>
  `${local.format(start, 'p', culture)} – ${local.format(end, 'p', culture)}`;

const timeRangeStartFormat: FormatterFn = ({ start }, culture, local) =>
  `${local.format(start, 'h:mma', culture)} – `;

const timeRangeEndFormat: FormatterFn = ({ end }, culture, local) =>
  ` – ${local.format(end, 'h:mma', culture)}`;

const weekRangeFormat: FormatterFn = ({ start, end }, culture, local) =>
  `${local.format(start, 'MMMM dd', culture)} – ${local.format(
    end,
    dayjs(start).isSame(end, 'month') ? 'dd' : 'MMMM dd',
    culture,
  )}`;

export const formats = {
  dateFormat: 'dd',
  dayFormat: 'dd eee',
  weekdayFormat: 'cccc',

  selectRangeFormat: timeRangeFormat,
  eventTimeRangeFormat: timeRangeFormat,
  eventTimeRangeStartFormat: timeRangeStartFormat,
  eventTimeRangeEndFormat: timeRangeEndFormat,

  timeGutterFormat: 'p',

  monthHeaderFormat: 'MMMM yyyy',
  dayHeaderFormat: 'cccc MMM dd',
  dayRangeHeaderFormat: weekRangeFormat,
  agendaHeaderFormat: dateRangeFormat,

  agendaDateFormat: 'ccc MMM dd',
  agendaTimeFormat: 'p',
  agendaTimeRangeFormat: timeRangeFormat,
};

const locales = {
  'en-US': enUS,
} as Record<string, Locale>;

type CustomLocalizer = (options: {
  startOfWeek: typeof startOfWeek;
  getDay: typeof getDay;
  format: typeof format;
  locales: typeof locales;
}) => DateLocalizer;
export const customCalendarLocalizer: CustomLocalizer = function ({
  startOfWeek,
  getDay,
  format,
  locales,
}) {
  return new DateLocalizer({
    // @ts-expect-error find out why the type is not compatible
    formats,
    firstOfWeek(culture) {
      return getDay(startOfWeek(new Date(), { locale: locales[culture] }));
    },

    format: (value, formatString, culture) => {
      const dateValue = value instanceof Date ? value : new Date(value as string | number);
      return format(dateValue, formatString, {
        locale: locales[culture!],
      });
    },
    inRange,
    startOfWeek,
    startOf,
    endOf,
    range,
  });
};

function fixUnit(unit?: string) {
  let datePart = unit ? unit.toLowerCase() : unit;
  if (datePart === 'FullYear') {
    datePart = 'year';
  } else if (!datePart) {
    datePart = undefined;
  }
  return datePart as OpUnitType;
}

function startOf(date: Date | null = null, unit: string) {
  const datePart = fixUnit(unit);
  if (datePart) {
    return dayjs(date).startOf(datePart).toDate();
  }
  return dayjs(date).toDate();
}

function endOf(date: Date | null = null, unit: string) {
  const datePart = fixUnit(unit);
  if (datePart) {
    return dayjs(date).endOf(datePart).toDate();
  }
  return dayjs(date).toDate();
}

// eslint-disable-next-line max-params
function inRange(day: Date, min: Date, max: Date, unit = 'day') {
  const datePart = fixUnit(unit);
  const djDay = dayjs(day);
  const djMin = dayjs(min);
  const djMax = dayjs(max);
  return djDay.isBetween(djMin, djMax, datePart, '[]');
}

function range(start: Date, end: Date, unit = 'day') {
  const datePart = fixUnit(unit);
  // because the add method will put these in tz, we have to start that way
  let current = dayjs(start).toDate();
  const days = [];

  while (lte(current, end, unit)) {
    days.push(current);
    current = add(current, 1, datePart);
  }

  return days;
}

function lte(a: Date, b: Date, unit: string) {
  const [dtA, dtB, datePart] = defineComparators(a, b, unit) as [Dayjs, Dayjs, ManipulateType];
  return dtA.isSameOrBefore(dtB, datePart);
}

function defineComparators(a: Date, b: Date, unit: string) {
  const datePart = fixUnit(unit);
  const dtA = datePart ? dayjs(a).startOf(datePart) : dayjs(a);
  const dtB = datePart ? dayjs(b).startOf(datePart) : dayjs(b);
  return [dtA, dtB, datePart];
}

function add(date: Date, adder: number, unit: string) {
  const datePart = fixUnit(unit) as ManipulateType;
  return dayjs(date).add(adder, datePart).toDate();
}

export const localizer = customCalendarLocalizer({
  format,
  startOfWeek,
  getDay,
  locales,
});
